<template>
  <a-modal
    :visible="visible"
    :title="modalTitle"
    width="800px"
    :closable="true"
    :mask-closable="true"
    :footer="null"
    @cancel="handleClose"
  >
    <div class="card-modal-content">
      <div class="modal-header">
        <div class="title-container">
          <!-- 병원명과 카드 관리 텍스트 제거 -->
        </div>
        <div class="action-container">
          <a-button 
            type="primary" 
            class="yellow-button"
            @click="showAddCardModal = true"
          >
            신용카드 등록
          </a-button>
        </div>
      </div>
      
      <div class="table-container">
        <div class="loading-container" v-if="loading">
          <a-spin />
        </div>
        
        <a-table
          v-else
          class="ant-table-striped"
          bordered
          size="small"
          :columns="columns"
          :data-source="cardList"
          :pagination="false"
          :locale="{ emptyText: '등록된 카드가 없습니다. 신용카드 등록 버튼을 눌러 카드를 추가해주세요.' }"
        >
          <!-- 상태 컬럼 -->
          <template #bodyCell="{ column, text, record }">
            <template v-if="column.dataIndex === 'status'">
              <a-tag :color="getStatusColor(record)">
                {{ getStatusText(record) }}
              </a-tag>
            </template>
            
            <!-- 기본 카드 컬럼 -->
            <template v-if="column.dataIndex === 'isDefault'">
              <a-switch
                :checked="record.isDefault"
                :disabled="record.isDefault || !record.isActive"
                @change="(checked) => handleDefaultChange(record, checked)"
              />
            </template>
            
            <!-- 활성화 컬럼 -->
            <template v-if="column.dataIndex === 'isActive'">
              <a-switch
                :checked="record.isActive"
                @change="(checked) => handleActiveChange(record, checked)"
              />
            </template>
            
            <!-- 작업 컬럼 -->
            <template v-if="column.dataIndex === 'actions'">
              <a-button
                type="primary"
                size="small"
                danger
                :disabled="record.isDefault"
                @click="() => handleDeleteCard(record)"
              >
                삭제
              </a-button>
            </template>
            
            <!-- 빌링키 컬럼 -->
            <template v-if="column.dataIndex === 'billingKey'">
              <div class="button-center">
                <a-button
                  v-if="record.isActive"
                  type="primary"
                  size="small"
                  class="yellow-button"
                  @click="() => handleAuthorizationCard(record)"
                >
                  {{ record.billingKey ? '재발급하기' : '발급하기' }}
                </a-button>
                <span v-else>-</span>
              </div>
            </template>
          </template>
        </a-table>
      </div>
    </div>
  </a-modal>
  
  <!-- 카드 추가 모달 -->
  <add-card-modal
    v-model:visible="showAddCardModal"
    :subscription="subscription"
    @card-added="handleCardAdded"
  />
</template>

<script>
import { ref, computed, watch } from 'vue';
import { getCurrentInstance } from 'vue';
import salesRecordsApis from '../api';
import AddCardModal from './AddCardModal.vue';
import { twoBtnDialog } from '@/utils/utils';

export default {
  name: 'CardManagementModal',
  components: {
    AddCardModal
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    subscription: {
      type: Object,
      default: () => ({})
    }
  },
  
  emits: ['update:visible', 'card-updated'],
  
  setup(props, { emit }) {
    const internalInstance = getCurrentInstance();
    const globalProperties = internalInstance?.appContext.config.globalProperties;
    
    // 상태 정의
    const loading = ref(false);
    const cardList = ref([]);
    const showAddCardModal = ref(false);
    
    // 모달 제목
    const modalTitle = computed(() => {
      return `카드 관리 - ${props.subscription.hospitalName || ''}`;
    });
    
    // 토스트 메시지 표시 유틸리티 함수
    const showToast = (type, message) => {
      if (globalProperties?.$message) {
        globalProperties.$message[type](message);
      } else {
        alert(message);
      }
    };
    
    // 테이블 컬럼 정의
    const columns = [
      {
        title: '번호',
        dataIndex: 'index',
        key: 'index',
        align: 'center',
        width: 60
      },
      {
        title: '카드번호',
        dataIndex: 'cardNumber',
        key: 'cardNumber',
        align: 'center',
        width: 180,
        customRender: ({ text }) => maskCardNumber(text)
      },
      {
        title: '카드사',
        dataIndex: 'cardCompany',
        key: 'cardCompany',
        align: 'center'
      },
      {
        title: '유효기간',
        dataIndex: 'cardExpiry',
        key: 'cardExpiry',
        align: 'center'
      },
      {
        title: '자동결제 빌링키 발급',
        dataIndex: 'billingKey',
        key: 'billingKey',
        align: 'center'
      },
      {
        title: '상태',
        dataIndex: 'status',
        key: 'status',
        align: 'center'
      },
      {
        title: '기본 카드',
        dataIndex: 'isDefault',
        key: 'isDefault',
        align: 'center'
      },
      {
        title: '활성화',
        dataIndex: 'isActive',
        key: 'isActive',
        align: 'center'
      },
      {
        title: '작업',
        dataIndex: 'actions',
        key: 'actions',
        align: 'center'
      }
    ];
    
    // 카드 번호 마스킹 함수
    const maskCardNumber = (cardNumber) => {
      if (!cardNumber) return '-';
      
      const digits = cardNumber.replace(/\D/g, '');
      if (digits.length < 8) return cardNumber;
      
      // 4자리-4자리-4자리-4자리 형식으로 포맷팅
      const formatted = [];
      for (let i = 0; i < digits.length; i += 4) {
        formatted.push(digits.substring(i, i + 4));
      }
      
      // 가운데 부분 마스킹 처리
      if (formatted.length >= 4) {
        formatted[1] = '****';
        formatted[2] = '****';
      } else if (formatted.length === 3) {
        formatted[1] = '****';
      }
      
      return formatted.join('-');
    };
    
    // 카드 상태 색상 가져오기
    const getStatusColor = (record) => {
      if (!record.isActive) return 'gray';
      if (record.isDefault) return 'green';
      return 'blue';
    };
    
    // 카드 상태 텍스트 가져오기
    const getStatusText = (record) => {
      if (!record.isActive) return '비활성';
      if (record.isDefault) return '기본카드';
      return '활성';
    };
    
    // 구독 정보 변경 시 카드 목록 업데이트
    watch(() => props.subscription, (newVal) => {
      if (newVal && newVal.id) {
        loadCardList();
      }
    }, { deep: true });
    
    // visible 속성 변경 확인
    watch(() => props.visible, (newVal) => {
      if (newVal && props.subscription && props.subscription.id) {
        loadCardList();
      }
    });
    
    // 카드 목록 로드 함수
    const loadCardList = async () => {
      if (!props.subscription || !props.subscription.id) {
        cardList.value = [];
        return;
      }
      
      loading.value = true;
      
      try {
        // 구독 정보에서 카드 목록 가져오기 대신 API로 최신 데이터 직접 조회
        const api = salesRecordsApis(globalProperties?.$apiInstance, globalProperties?.$store);
        const response = await api.getCenterflowSubscription(
          props.subscription.id,
          (response) => {
            const subscriptionData = response.data;
            
            if (subscriptionData.cardInfos && subscriptionData.cardInfos.length > 0) {
              // 카드 정보에 인덱스 추가 및 데이터 복사
              cardList.value = subscriptionData.cardInfos.map((card, index) => {
                // 원본 카드 데이터 복사
                const formattedCard = { ...card };
                // 인덱스 추가
                formattedCard.index = index + 1;
                // 키 추가 (고유 식별자, 테이블 렌더링 최적화용)
                formattedCard.key = `card-${index}`;
                return formattedCard;
              });
            } else {
              cardList.value = [];
            }
          },
          (error) => {
            showToast('error', '카드 목록을 불러오는데 실패했습니다.');
            cardList.value = [];
          }
        );
      } catch (error) {
        showToast('error', '카드 목록을 불러오는데 실패했습니다.');
        cardList.value = [];
      } finally {
        loading.value = false;
      }
    };
    
    // 모달 닫기 핸들러
    const handleClose = () => {
      emit('update:visible', false);
    };
    
    // 카드 추가 핸들러
    const handleCardAdded = () => {
      loadCardList();
      emit('card-updated');
    };
    
    // 기본 카드 변경 핸들러
    const handleDefaultChange = async (record, checked) => {
      if (!checked) return; // 기본 카드는 해제 불가
      
      loading.value = true;
      
      try {
        // API 인스턴스 가져오기
        const api = salesRecordsApis(globalProperties?.$apiInstance, globalProperties?.$store);
        
        // 기본 카드 설정 API 호출
        await api.setDefaultCenterflowCard(
          props.subscription.id,
          record.cardNumber,
          (response) => {
            showToast('success', '기본 카드가 변경되었습니다.');
            
            // 카드 목록 다시 로드
            loadCardList();
            
            // 부모 컴포넌트에 업데이트 알림
            emit('card-updated');
          },
          (error) => {
            showToast('error', '기본 카드 변경에 실패했습니다.');
          }
        );
      } catch (error) {
        showToast('error', '기본 카드 변경 중 오류가 발생했습니다.');
      } finally {
        loading.value = false;
      }
    };
    
    // 카드 활성화 상태 변경 핸들러
    const handleActiveChange = async (record, checked) => {
      loading.value = true;
      
      try {
        // API 인스턴스 가져오기
        const api = salesRecordsApis(globalProperties?.$apiInstance, globalProperties?.$store);
        
        // 카드 활성화/비활성화 API 호출
        await api.toggleCenterflowCardActive(
          props.subscription.id,
          record.cardNumber,
          checked,
          (response) => {
            showToast('success', `카드가 ${checked ? '활성화' : '비활성화'}되었습니다.`);
            
            // 카드 목록 다시 로드
            loadCardList();
            
            // 부모 컴포넌트에 업데이트 알림
            emit('card-updated');
          },
          (error) => {
            showToast('error', `카드 ${checked ? '활성화' : '비활성화'}에 실패했습니다.`);
          }
        );
      } catch (error) {
        showToast('error', '카드 상태 변경 중 오류가 발생했습니다.');
      } finally {
        loading.value = false;
      }
    };
    
    // 카드 삭제 핸들러
    const handleDeleteCard = async (record) => {
      // 기본 확인 대화상자 표시
      const isConfirmed = await twoBtnDialog(
        '카드 삭제',
        `정말로 이 카드를 삭제하시겠습니까?`,
        '삭제'
      );
      
      if (!isConfirmed) return;
      
      loading.value = true;
      
      try {
        // API 인스턴스 가져오기
        const api = salesRecordsApis(globalProperties?.$apiInstance, globalProperties?.$store);
        
        // 삭제 옵션
        const deleteOptions = {
          reason: '사용자 요청으로 삭제',
          forceDeleteDefault: record.isDefault
        };
        
        // 기본 카드 삭제 시 추가 확인
        if (record.isDefault) {
          const isDefaultConfirmed = await twoBtnDialog(
            '기본 카드 삭제',
            '기본 카드를 삭제하시겠습니까? 다른 카드가 기본 카드로 설정됩니다.',
            '삭제'
          );
          
          if (!isDefaultConfirmed) {
            loading.value = false;
            return;
          }
        }
        
        // 카드 삭제 API 호출
        await api.removeCenterflowCardInfo(
          props.subscription.id,
          record.cardNumber,
          deleteOptions,
          async (response) => {
            // 카드 삭제 성공 시 처리
            showToast('success', '카드가 성공적으로 삭제되었습니다.');
            
            // 1. 카드 삭제 후 구독 정보를 직접 다시 로드
            try {
              await api.getCenterflowSubscription(
                props.subscription.id,
                (refreshResponse) => {
                  // 구독 객체 전체를 복사하여 부모 컴포넌트의 데이터도 갱신되도록 함
                  Object.assign(props.subscription, refreshResponse.data);
                  
                  // 2. 카드 목록 다시 로드
                  loadCardList();
                  
                  // 3. 부모 컴포넌트에 업데이트 알림
                  emit('card-updated');
                },
                (error) => {
                  console.error('구독 정보 새로고침 실패:', error);
                }
              );
            } catch (error) {
              console.error('구독 정보 새로고침 중 오류:', error);
            }
          },
          (error) => {
            // 오류 메시지 표시
            const errorMessage = error.response?.data?.message || '카드 삭제에 실패했습니다.';
            
            // 기본 카드 삭제 오류 특별 처리
            if (errorMessage.includes('기본 카드는 삭제할 수 없습니다')) {
              showToast('warning', '기본 카드는 삭제할 수 없습니다. 먼저, 다른 카드를 기본 카드로 설정해주세요.');
            } 
            // 마지막 카드 삭제 오류 특별 처리
            else if (errorMessage.includes('마지막 카드는 삭제할 수 없습니다')) {
              showToast('warning', '마지막 카드는 삭제할 수 없습니다. 최소 1개의 카드가 필요합니다.');
            }
            else {
              showToast('error', errorMessage);
            }
          }
        );
      } catch (error) {
        showToast('error', '카드 삭제 중 오류가 발생했습니다.');
      } finally {
        loading.value = false;
      }
    };
    
    // 빌링키 발급/재발급 핸들러
    const handleAuthorizationCard = async (record) => {
      if (!record.isActive) {
        showToast('warning', '비활성화된 카드는 빌링키를 발급할 수 없습니다.');
        return;
      }
      
      const action = record.billingKey ? '재발급' : '발급';
      const isConfirmed = await twoBtnDialog(
        `빌링키 ${action}`,
        `이 카드의 자동결제 빌링키를 ${action}하시겠습니까?`,
        action
      );
      
      if (!isConfirmed) return;
      
      loading.value = true;
      
      try {
        // API 인스턴스 가져오기
        const api = salesRecordsApis(globalProperties?.$apiInstance, globalProperties?.$store);
        
        // 빌링키 발급 요청 데이터 구성 - 백엔드가 기대하는 필드명으로 변경
        const cardData = {
          subscriptionId: props.subscription.id,
          cNo: props.subscription.businessNumber,
          cardNo: record.cardNumber,
          expiration: record.cardExpiry,
          identificationNo: record.birthDate,
        };
        
        console.log('빌링키 발급 요청 데이터:', cardData);
        
        // 빌링키 발급 API 호출 - api.js에 정의된 checkCardValidity 메서드를 사용
        await api.checkCardValidity(
          cardData,
          (response) => {
            showToast('success', `빌링키가 성공적으로 ${action}되었습니다.`);
            
            // 구독 정보 다시 로드
            loadCardList();
            emit('card-updated');
          },
          (error) => {
            const errorMessage = error.response?.data?.message || `빌링키 ${action}에 실패했습니다.`;
            showToast('error', errorMessage);
          }
        );
      } catch (error) {
        showToast('error', `빌링키 ${action} 중 오류가 발생했습니다.`);
        console.error('빌링키 발급 오류:', error);
      } finally {
        loading.value = false;
      }
    };
    
    return {
      loading,
      cardList,
      columns,
      modalTitle,
      showAddCardModal,
      maskCardNumber,
      getStatusColor,
      getStatusText,
      handleClose,
      handleCardAdded,
      handleDefaultChange,
      handleActiveChange,
      handleDeleteCard,
      handleAuthorizationCard,
      loadCardList
    };
  }
};
</script>

<style scoped lang="scss">
.card-modal-content {
  padding: 10px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.title-container {
  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: bold;
  }
}

.action-container {
  button {
    font-size: 14px;
  }
}

.table-container {
  position: relative;
  min-height: 200px;
}

.loading-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.7);
  z-index: 1;
}

.button-center {
  display: flex;
  justify-content: center;
  width: 100%;
}

:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;
  
  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }
  
  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}
</style>
